<template>
    <div class="flex flex-col gap-y-4">
        <form-element title="Filename Selection">
            <donation-select v-model="fileDefinition" :disabled="!fileDefinitions.length">
                <template #label>
                    {{ fileDefinition ? `${fileDefinition.segment_name} - ${fileDefinition.name}` : fileDefinitions.length ? 'Select...' : 'No filenames'}}
                </template>

                <select-option
                    v-for="fileDefinition in fileDefinitions"
                    :key="fileDefinition.id"
                    :value="fileDefinition"
                >
                    {{ `${fileDefinition.segment_name} - ${fileDefinition.name}` }}
                </select-option>
            </donation-select>

            <template #accessory>
                <button v-if="fileDefinition" class="text-cool-gray flex items-center gap-x-1 text-sm" @click="clear()">
                    <CloseRectangleFillIcon class="-mt-0.5" />
                    <span>Clear Selection</span>
                </button>
            </template>
        </form-element>

        <RadioGroup v-if="fileDefinition" v-model="fileSuggestionID" class="flex flex-col space-y-2 text-lg">
            <RadioGroupLabel class="sr-only">Filenames for {{ fileDefinition.name }}</RadioGroupLabel>
            <RadioGroupOption
                v-slot="{ checked }"
                v-for="suggestion in fileSuggestions"
                :key="suggestion.id"
                :value="suggestion.id"
                class="flex space-x-2"
            >
                <span class="flex w-6 h-6 bg-gradient-to-br from-purple to-blue rounded-full items-center justify-center">
                    <span class="block rounded-full w-5 h-5" :class="[
                        checked ? 'border-4 border-dark-blue' : 'bg-dark-blue'
                    ]"></span>
                </span>
                <span class="text-white">{{ suggestion.name }} - <span class="text-light-green">{{ formatMoney(suggestion.donation_total) }}</span></span>
            </RadioGroupOption>
            <div class="flex space-x-2">
                <RadioGroupOption
                    v-slot="{ checked }"
                    :value="-1"
                >
                    <span class="flex w-6 h-6 bg-gradient-to-br from-purple to-blue rounded-full items-center justify-center">
                        <span class="block rounded-full w-5 h-5" :class="[
                            checked ? 'border-4 border-dark-blue' : 'bg-dark-blue'
                        ]"></span>
                    </span>
                </RadioGroupOption>
                <input v-model="customFilename" :maxlength="fileDefinition.max_length ?? 230" type="text" placeholder="Suggest your own..." class="w-full bg-transparent border-b-2 text-white border-purple">
            </div>
        </RadioGroup>
    </div>
</template>

<script setup>
import { ref, onMounted, inject, watch } from 'vue';
import {
    RadioGroup,
    RadioGroupLabel,
    RadioGroupOption,
} from '@headlessui/vue';
import { formatMoney } from '../../utils';
import DonationSelect from './ui/Select.vue';
import SelectOption from './ui/SelectOption.vue';
import FormElement from './ui/FormElement.vue';
import CloseRectangleFillIcon from './icons/CloseRectangleFillIcon.vue';

const fileDefinitions = ref([]);
const fileDefinition = inject('fileDefinition');
const fileSuggestions = ref([]);
const fileSuggestionID = ref(null);
const fileSuggestion = inject('fileSuggestion');
const customFilename = inject('customFilename');

onMounted(() => {
    fetch('/file-definitions')
        .then(response => response.json())
        .then(value => {
            fileDefinitions.value = value.filter((definition) => definition.accepts_filenames);
        })
})

const clear = () => {
    fileDefinition.value = null;
    fileSuggestionID.value = null;
    customFilename.value = null;
}

const abortController = ref(null);
watch(fileDefinition, (newValue) => {
    if (abortController.value) {
        abortController.value.abort();
    }

    if (newValue) {
        fileSuggestions.value = [];

        abortController.value = new AbortController();

        fetch(`/file-definitions/${newValue.id}/file-suggestions`, { signal: abortController.value.signal })
            .then(response => response.json())
            .then(value => {
                let id = 1;
                fileSuggestions.value = value.map(item => {
                    return {
                        ...item,
                        id: ++id
                    }
                });
            })
        .catch(() => { /* Do nothing */});
    }
});

watch(fileSuggestionID, (id) => {
    if (!id) {
        fileSuggestion.value = null;
        return;
    }

    if (id == -1) {
        fileSuggestion.value = {
            id: -1,
            name: 'custom'
        }
    } else {
        fileSuggestion.value = fileSuggestions.value.find(suggestion => {
            return suggestion.id == id;
        })
    }
})

watch(customFilename, (newValue) => {
    if (newValue) {
        fileSuggestionID.value = -1;
    }
})
</script>